import { useState, useEffect, useCallback } from 'react';
import useFormValidation from '@/hooks/useFormValidation';
import {
  toCredentialPayload,
  toEmptyForm,
  toScopeOptions,
  toStoredForm,
} from './oauthCredentialForm.utils';

const useOAuthCredentialForm = ({
  data,
  settingsFields,
  showTokenUrl,
  scopeOptions,
  onSave,
}) => {
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(() =>
    toEmptyForm(settingsFields, scopeOptions),
  );
  const [availableScopes, setAvailableScopes] = useState(scopeOptions ?? []);

  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearFieldError,
    clearAllErrors,
  } = useFormValidation();

  const applyData = useCallback(
    (nextData) => {
      setCurrent(nextData);
      if (!nextData?.credential) {
        setForm(toEmptyForm(settingsFields, scopeOptions));
        return;
      }
      setForm(toStoredForm(settingsFields, nextData.credential));
      if (scopeOptions) {
        setAvailableScopes(
          toScopeOptions(scopeOptions, nextData.credential.scopes),
        );
      }
    },
    [settingsFields, scopeOptions],
  );

  useEffect(() => {
    applyData(data);
  }, [data, applyData]);

  const readOnly = current?.read_only ?? false;

  const changeField = (name, value) => {
    setForm((previous) =>
      Object.assign({}, previous, Object.fromEntries([[name, value]])),
    );
    clearFieldError(name);
  };

  const changeSetting = (name, value) => {
    setForm((previous) =>
      Object.assign({}, previous, {
        settings: Object.assign(
          {},
          previous.settings,
          Object.fromEntries([[name, value]]),
        ),
      }),
    );
    clearFieldError(name);
  };

  const save = async () => {
    if (readOnly) return;
    clearAllErrors();

    try {
      const savedData = await onSave(toCredentialPayload(form, showTokenUrl));
      applyData(savedData);
    } catch (error) {
      if (error.response?.status === 400) {
        setBackendErrors(error.response?.data);
      }
    }
  };

  return {
    form,
    availableScopes,
    readOnly,
    hasCredential: Boolean(current?.credential),
    hasClientSecret: current?.credential?.has_client_secret ?? false,
    fieldErrors,
    generalErrors,
    changeField,
    changeSetting,
    save,
  };
};

export default useOAuthCredentialForm;
